<template>
  <div class='advanced-pagination'>
    <div class='advanced-pagination__jump' v-if='isShowPagination'>
        <span class='advanced-pagination__jump__label'>
          {{ $t('go.to.page.label') }}
        </span>
      <div class='advanced-pagination__jump__select'>
        <v-text-field
          @change='pageInput'
          v-model="manuallyPageInput"
          type='number'
          dense
          outlined
          required
          hide-spin-buttons
        />
      </div>
    </div>
    <div class='advanced-pagination__pagination' v-if='isShowPagination'>
      <v-btn
        v-if='isShowFirstLastPage'
        class='advanced-pagination__pagination__get-first go-to-first-btn'
        :class='{"disabled": goToFirstPageDisabled}'
        icon
        :disabled='goToFirstPageDisabled'
        @click='goToPage(1)'
      >
        <v-icon>first_page</v-icon>
      </v-btn>
      <v-pagination
        :key='paginationKey'
        v-if='itemsPerPage && pagesAmount'
        v-model='currentPage'
        :length='pagesAmount'
        :total-visible='totalVisible'
        @input='goToPage(currentPage)'
      />
      <v-btn
        v-if='isShowFirstLastPage'
        class='advanced-pagination__pagination__get-last go-to-last-btn'
        :class='{"disabled": goToLastPageDisabled}'
        icon
        :disabled='goToLastPageDisabled'
        @click='goToPage(pagesAmount)'
      >
        <v-icon>last_page</v-icon>
      </v-btn>
    </div>
    <div class='advanced-pagination__items_amount_options' :class='{"not-show-pagination": !isShowPagination}'>
      <span class='advanced-pagination__items_amount_options__label'>
        {{ $t('results.per.page.label') }}
      </span>
      <div class='advanced-pagination__items_amount_options__select'>
        <v-select
          :value='itemsPerPage'
          :items='itemsPerPageOptions'
          solo
          dense
          @input='changedItemsAmountPerPage'
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AdvancedPagination',
  props: {
    isShowPagination: {
      type: Boolean,
      default: true,
      required: false,
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [20, 30, 50],
      required: false,
    },
    tab: {
      type: String,
      required: true,
    },
    pagesAmount: {
      type: Number,
      default: 1,
      required: true,
    },
    totalVisible: {
      type: Number,
      default: 10,
    },
    page: {
      type: Number,
      default: 1,
      required: false,
    },
    resetPage: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    ...mapGetters(['paginationItemsPerPageByTabs']),
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs[this.tab];
    },
    isShowFirstLastPage() {
      return this.pagesAmount > 2 && this.totalVisible > 2;
    },
    goToLastPageDisabled() {
      return this.currentPage === this.pagesAmount;
    },
    goToFirstPageDisabled() {
      return this.currentPage === 1;
    },
  },
  watch: {
    resetPage() {
      this.currentPage = 1;
      this.$emit('pagedRested');
    },
    itemsPerPage() {
      this.paginationKey += 1;
    },
    pagesAmount() {
      this.currentPage = 1;
      this.paginationKey += 1;
    },
    currentPage() {
      this.manuallyPageInput = this.currentPage;
    },
    tab() {
      if (this.tab === 'bucket' || this.tab === 'cloud' || this.tab === 'services') {
        this.currentPage = this.page;
      }
    },
  },
  methods: {
    async pageInput() {
      if (this.manuallyPageInput.length &&
        !Number.isNaN(this.manuallyPageInput) &&
        this.manuallyPageInput <= this.pagesAmount &&
        this.manuallyPageInput >= 1) {
        await this.goToPage(parseInt(this.manuallyPageInput, 10));
      } else {
        this.manuallyPageInput = this.currentPage;
      }
    },
    async goToPage(page) {
      this.currentPage = page;
      await this.$emit('changedSelectedPage', this.currentPage);
    },
    async changedItemsAmountPerPage(selectedItemsPerPageOptions) {
      this.currentPage = 1;
      await this.$emit('changedItemsAmountPerPage', selectedItemsPerPageOptions);
      this.paginationKey += 1;
    },
  },
  data() {
    return {
      manuallyPageInput: this.page || 1,
      paginationKey: 0,
      currentPage: this.page || 1,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.advanced-pagination {
  display: flex;
  margin-top: $space-6;
  margin-bottom: $space-6;
  align-items: center;
  padding: 0 $space-10;
  justify-content: space-between;

  // Pagination navigation buttons
  &__pagination {
    display: flex;
    align-items: center;

    &__get-first,
    &__get-last {
      box-shadow: $shadow-sm;
      border-radius: $radius-md;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      text-decoration: none;
      height: $pagination-item-size;
      width: $pagination-item-size;
      margin: $space-1 $space-2;
    }
  }

  // Go to page input
  &__jump {
    display: flex;
    align-items: center;

    &__label {
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: $font-size-base;
      letter-spacing: $letter-spacing-wide;
      line-height: $line-height-tight;
    }

    &__select {
      margin-left: $space-1;
      max-width: rem(70);
      min-width: rem(70);
    }
  }

  // Items per page selector
  &__items_amount_options {
    display: flex;
    align-items: center;

    &__label {
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: $font-size-base;
      letter-spacing: $letter-spacing-wide;
      line-height: $line-height-tight;
    }

    &__select {
      margin-left: $space-1;
      max-width: rem(70);
      min-width: rem(70);
    }

    &.not-show-pagination {
      position: absolute;
      right: rem(90);
    }
  }

  // Vuetify overrides - using :deep() for child components
  :deep(.v-input__control) {
    min-height: $btn-height-md;
    min-width: rem(75);
  }

  :deep(.v-text-field.v-text-field--enclosed > .v-input__control > .v-input__slot),
  :deep(.v-text-field.v-text-field--enclosed .v-text-field__details) {
    padding: 0;
  }

  // Remove gray background from solo select
  :deep(.v-select.v-select .v-select__slot),
  :deep(.v-select.v-select .v-input__slot),
  :deep(.v-text-field.v-text-field--solo .v-input__slot) {
    background: transparent !important;
    background-color: transparent !important;
  }

  :deep(.v-ripple__container) {
    opacity: 0;
  }

  // First/Last page buttons - disabled state
  :deep(.go-to-first-btn.v-btn--disabled),
  :deep(.go-to-last-btn.v-btn--disabled) {
    opacity: 0.6;
    pointer-events: none;
    background-color: initial;
    color: initial;

    .v-icon {
      color: rgba(0, 0, 0, 0.54);
    }
  }

  // First/Last page buttons - focus/hover states
  :deep(.go-to-first-btn.v-btn),
  :deep(.go-to-last-btn.v-btn) {
    &:focus::before,
    &:focus::after {
      opacity: 0;
    }

    &:hover::before {
      background-color: initial;
      color: initial;
      border-color: initial;
    }

    &::before {
      transition: opacity 0s;
    }
  }
}

// Dark mode support
[data-theme="dark"] {
  .advanced-pagination {
    &__jump__label,
    &__items_amount_options__label {
      color: var(--text-primary);
    }

    // Fix pagination arrow colors in dark mode
    :deep(.v-pagination) {
      .v-pagination__navigation {
        background-color: var(--bg-tertiary);

        .v-icon {
          color: var(--text-primary);
        }
      }

      .v-pagination__item {
        background-color: var(--bg-tertiary);
        color: var(--text-primary);

        &--active {
          background-color: var(--brand-primary);
          color: var(--text-inverse);
        }
      }
    }

    // Fix first/last page button icons
    :deep(.go-to-first-btn),
    :deep(.go-to-last-btn) {
      background-color: var(--bg-tertiary);

      .v-icon {
        color: var(--text-primary);
      }

      &.v-btn--disabled .v-icon {
        color: var(--text-muted);
      }
    }
  }
}
</style>
